/**
 * Cloudinary URL helper — adds on-the-fly transformations for performance & SEO.
 *
 * Usage:
 *   cld(url, { w: 1200, h: 800, crop: "fill" })
 *   cld(url, "card")   // preset
 *
 * Presets:
 *   "hero"      → 1920w, auto format, smart quality
 *   "wide"      → 1400w
 *   "card"      → 800w
 *   "thumb"     → 400w
 *   "square"    → 600x600 fill
 *
 * For non-Cloudinary URLs the original URL is returned unchanged.
 */

const PRESETS = {
  hero:        "f_auto,q_auto:eco,w_1600,c_limit",
  wide:        "f_auto,q_auto:eco,w_1100,c_limit",
  card:        "f_auto,q_auto:eco,w_700,c_limit",
  thumb:       "f_auto,q_auto:eco,w_280,c_limit",
  square:      "f_auto,q_auto:eco,w_480,h_480,c_fill",
  // Video presets — Cloudinary adaptive delivery (auto-format VP9/H264/AV1, auto-quality)
  video:       "f_auto,q_auto:eco,w_1100,c_limit",
  video_card:  "f_auto,q_auto:eco,w_700,c_limit",
  video_mobile:"f_auto,q_auto:eco,w_540,c_limit",
  // Auto-generated poster image from video (first frame, JPEG)
  poster:      "f_jpg,q_auto:eco,w_700,so_1.5,c_limit",
};

function buildString(opts) {
  if (typeof opts === "string" && PRESETS[opts]) return PRESETS[opts];
  if (typeof opts === "string") return opts;
  const parts = ["f_auto", "q_auto:eco"];
  if (opts.w) parts.push(`w_${opts.w}`);
  if (opts.h) parts.push(`h_${opts.h}`);
  if (opts.crop) parts.push(`c_${opts.crop}`);
  return parts.join(",");
}

export function cld(url, opts = "card") {
  if (!url || typeof url !== "string") return url;
  // Only transform Cloudinary URLs
  if (!url.includes("res.cloudinary.com")) return url;
  // URL format: https://res.cloudinary.com/<cloud>/<resource>/upload/<existing-transforms?>/<rest>
  const marker = "/upload/";
  const idx = url.indexOf(marker);
  if (idx === -1) return url;
  const head = url.slice(0, idx + marker.length);
  const tail = url.slice(idx + marker.length);
  // Strip any existing transformations (segment before the next /vNNN/ or before the public_id)
  const tailParts = tail.split("/");
  // If the first segment is a version like "v1234567" we leave it; otherwise it might be transforms — replace those.
  let rest;
  if (tailParts[0].startsWith("v") && /^v\d+$/.test(tailParts[0])) {
    rest = tail;
  } else if (tailParts[0].match(/[a-z]_/)) {
    // existing transformations — drop them
    rest = tailParts.slice(1).join("/");
  } else {
    rest = tail;
  }
  return `${head}${buildString(opts)}/${rest}`;
}
